
import PropTypes from 'prop-types';
import { useState, useEffect, useCallback } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    Avatar,
    Typography,
    IconButton,
    TextField,
    Box,
    Chip,
    Autocomplete,
    Menu,
    MenuItem,
    Switch,
    FormControlLabel
} from '@mui/material';
import { LoadingButton } from '@mui/lab';

import UploadAvatar from '../../components/hook-form/upload/UploadAvatar';
import Iconify from '../../components/Iconify';
import axios from '../../utils/axios';
import { HOST_API } from '../../config';
import useAuth from '../../hooks/useAuth';
import { useDispatch } from '../../redux/store';
import { upsertConversation } from '../../redux/slices/internalChat';

GroupInfoModal.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    chat: PropTypes.object
};

export default function GroupInfoModal({ open, onClose, chat }) {
    const { user } = useAuth();
    const dispatch = useDispatch();



    // State for viewing members
    const [participants, setParticipants] = useState([]);
    const [groupName, setGroupName] = useState('');

    // State for adding members
    const [isAdding, setIsAdding] = useState(false);
    const [allUsers, setAllUsers] = useState([]);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (chat && open) {
            setParticipants(chat.participants || []);
            setGroupName(chat.name || '');
            setIsAdding(false);
            setSelectedUsers([]);
        }
    }, [chat, open]);

    const handleFetchUsers = async () => {
        try {
            const response = await axios.get('/api/user/list');
            // Filter out users already in the group
            const existingIds = chat.participants.map(p => p.userId._id);
            const available = (response.data.users || response.data).filter(u => !existingIds.includes(u._id));
            setAllUsers(available);
        } catch (error) {
            console.error(error);
        }
    };

    const handleAddMembers = async () => {
        if (selectedUsers.length === 0) return;
        setLoading(true);
        try {
            const response = await axios.put(`/api/internal-chat/${chat._id}`, {
                addMemberIds: selectedUsers.map(u => u._id)
            });
            dispatch(upsertConversation(response.data)); // Update Redux with new chat state
            setParticipants(response.data.participants);
            setIsAdding(false);
            setSelectedUsers([]);
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    // Confirmation Modal State
    const [openConfirm, setOpenConfirm] = useState(false);
    const [confirmType, setConfirmType] = useState(null); // 'leave' | 'remove'
    const [memberToRemove, setMemberToRemove] = useState(null);

    const handleRemoveMemberClick = (memberId) => {
        setMemberToRemove(memberId);
        setConfirmType('remove');
        setOpenConfirm(true);
        handleCloseMenu();
    };

    const handleLeaveGroupClick = () => {
        setConfirmType('leave');
        setOpenConfirm(true);
    };

    const handleConfirmAction = () => {
        if (confirmType === 'leave') {
            handleLeaveGroup();
        } else if (confirmType === 'remove' && memberToRemove) {
            handleRemoveMember(memberToRemove);
        }
        setOpenConfirm(false);
    };

    const handleRemoveMember = async (memberId) => {
        try {
            const response = await axios.put(`/api/internal-chat/${chat._id}`, {
                removeMemberIds: [memberId]
            });
            dispatch(upsertConversation(response.data));
            setParticipants(response.data.participants);
        } catch (error) {
            console.error(error);
        }
    };

    const handleLeaveGroup = async () => {
        try {
            await axios.put(`/api/internal-chat/${chat._id}`, {
                removeMemberIds: [user._id]
            });
            onClose();
        } catch (error) {
            console.error(error);
        }
    };

    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedMember, setSelectedMember] = useState(null);

    const currentUserParticipant = chat?.participants?.find(p => p.userId._id === user._id);
    const isAdmin = currentUserParticipant?.isAdmin;

    const handleOpenMenu = (event, member) => {
        setAnchorEl(event.currentTarget);
        setSelectedMember(member);
    };

    const handleCloseMenu = () => {
        setAnchorEl(null);
        setSelectedMember(null);
    };

    const handlePromoteAdmin = async () => {
        if (!selectedMember) return;
        try {
            const response = await axios.put(`/api/internal-chat/${chat._id}`, {
                promoteMemberIds: [selectedMember.userId._id]
            });
            dispatch(upsertConversation(response.data));
            setParticipants(response.data.participants);
            handleCloseMenu();
        } catch (error) {
            console.error(error);
        }
    };

    const handleDemoteAdmin = async () => {
        if (!selectedMember) return;
        try {
            const response = await axios.put(`/api/internal-chat/${chat._id}`, {
                demoteMemberIds: [selectedMember.userId._id]
            });
            dispatch(upsertConversation(response.data));
            setParticipants(response.data.participants);
            handleCloseMenu();
        } catch (error) {
            console.error(error);
        }
    };

    const handleAvatarDrop = useCallback(
        async (acceptedFiles) => {
            const file = acceptedFiles[0];
            if (file) {
                try {
                    const formData = new FormData();
                    formData.append('attachment', file);
                    const response = await axios.put(`/api/internal-chat/${chat._id}`, formData, {
                        headers: { 'Content-Type': 'multipart/form-data' }
                    });
                    dispatch(upsertConversation(response.data));
                } catch (error) {
                    console.error(error);
                }
            }
        },
        [chat, dispatch]
    );

    const handleUpdateName = async () => {
        if (groupName === chat.name || !groupName.trim()) return;
        try {
            const response = await axios.put(`/api/internal-chat/${chat._id}`, {
                name: groupName
            });
            dispatch(upsertConversation(response.data));
            // Optional: Show success snackbar
        } catch (error) {
            console.error('Failed to update group name:', error);
            setGroupName(chat.name); // Revert on error
        }
    };

    const handleToggleMode = async (event) => {
        const newMode = event.target.checked ? 'announcement' : 'normal';
        try {
            await axios.put(`/api/internal-chat/${chat._id}/mode`, {
                mode: newMode
            });
            // Socket will update the UI, but we can also update local state if needed via dispatch 
            // dispatch(updateChatMode({ chatId: chat._id, mode: newMode })); // If we imported the action
        } catch (error) {
            console.error('Failed to update mode:', error);
        }
    };

    if (!chat) return null;

    const isGroup = chat.type === 'group';

    return (
        <>
            <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
                {/* ... existing dialog content ... */}
                <DialogTitle>
                    {isGroup ? `Grupo: ${chat.name}` : 'Detalhes da Conversa'}
                </DialogTitle>

                <DialogContent>
                    {!isAdding ? (
                        <>
                            {isGroup && (
                                <Box sx={{ mb: 3, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                    <UploadAvatar
                                        file={chat.avatar ? `${HOST_API}${chat.avatar}` : null}
                                        onDrop={handleAvatarDrop}
                                        disabled={!isAdmin}
                                        helperText={
                                            isAdmin && (
                                                <Typography variant="caption" sx={{ mt: 2, mx: 'auto', display: 'block', textAlign: 'center', color: 'text.secondary' }}>
                                                    Toque para alterar a foto do grupo
                                                </Typography>
                                            )
                                        }
                                    />
                                    {isAdmin ? (
                                        <TextField
                                            fullWidth
                                            size="small"
                                            value={groupName}
                                            onChange={(e) => setGroupName(e.target.value)}
                                            onBlur={() => {
                                                if (groupName !== chat.name) {
                                                    handleUpdateName();
                                                }
                                            }}
                                            placeholder="Nome do Grupo"
                                            sx={{ mt: 2, maxWidth: 300 }}
                                            InputProps={{
                                                sx: {
                                                    typography: 'h6',
                                                    textAlign: 'center',
                                                    '& input': { textAlign: 'center' }
                                                }
                                            }}
                                        />
                                    ) : (
                                        <Typography variant="h6" sx={{ mt: 2 }}>
                                            {chat.name}
                                        </Typography>
                                    )}
                                </Box>
                            )}
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <Typography variant="subtitle1">Participantes ({participants.length})</Typography>
                                {isGroup && (
                                    <Button
                                        size="small"
                                        startIcon={<Iconify icon="eva:person-add-fill" />}
                                        onClick={() => {
                                            setIsAdding(true);
                                            handleFetchUsers();
                                        }}
                                    >
                                        Adicionar
                                    </Button>
                                )}
                            </Box>

                            {isGroup && isAdmin && (
                                <Box sx={{ mb: 2, p: 2, bgcolor: 'background.neutral', borderRadius: 1 }}>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={chat.mode === 'announcement'}
                                                onChange={handleToggleMode}
                                                name="mode"
                                            />
                                        }
                                        label={
                                            <Box>
                                                <Typography variant="subtitle2">Modo Comunicados</Typography>
                                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                                    Apenas administradores podem enviar mensagens
                                                </Typography>
                                            </Box>
                                        }
                                    />
                                </Box>
                            )}

                            <List dense>
                                {participants.map((p, index) => (
                                    <ListItem
                                        key={`${p.userId._id}_${index}`}
                                        secondaryAction={
                                            isGroup && isAdmin && p.userId._id !== user._id && (
                                                <IconButton edge="end" onClick={(e) => handleOpenMenu(e, p)}>
                                                    <Iconify icon="eva:more-vertical-fill" />
                                                </IconButton>
                                            )
                                        }
                                    >
                                        <ListItemAvatar>
                                            <Avatar alt={p.userId.name} src={p.userId.avatar ? `${HOST_API}${p.userId.avatar}` : ''}>
                                                {p.userId.name?.charAt(0)}
                                            </Avatar>
                                        </ListItemAvatar>
                                        <ListItemText
                                            primary={
                                                <Typography variant="subtitle2">
                                                    {p.userId.name}
                                                    {p.isAdmin && (
                                                        <Typography component="span" variant="caption" sx={{ color: 'text.secondary', ml: 1 }}>
                                                            (Admin)
                                                        </Typography>
                                                    )}
                                                </Typography>
                                            }
                                            secondary={p.userId.email}
                                        />
                                    </ListItem>
                                ))}
                            </List>

                            <Menu
                                anchorEl={anchorEl}
                                open={Boolean(anchorEl)}
                                onClose={handleCloseMenu}
                            >
                                <MenuItem onClick={selectedMember?.isAdmin ? handleDemoteAdmin : handlePromoteAdmin}>
                                    {selectedMember?.isAdmin ? 'Remover Admin' : 'Tornar Admin'}
                                </MenuItem>
                                <MenuItem onClick={() => handleRemoveMemberClick(selectedMember?.userId._id)} sx={{ color: 'error.main' }}>
                                    Remover do Grupo
                                </MenuItem>
                            </Menu>
                        </>
                    ) : (
                        <Box sx={{ mt: 2 }}>
                            <Typography variant="subtitle2" sx={{ mb: 2 }}>Adicionar Novos Membros</Typography>
                            <Autocomplete
                                multiple
                                options={allUsers}
                                getOptionLabel={(option) => option.name}
                                onChange={(event, newValue) => setSelectedUsers(newValue)}
                                renderInput={(params) => (
                                    <TextField {...params} label="Selecione usuários" placeholder="Buscar..." />
                                )}
                                renderTags={(value, getTagProps) =>
                                    value.map((option, index) => (
                                        <Chip {...getTagProps({ index })} key={option._id} size="small" label={option.name} />
                                    ))
                                }
                            />
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, gap: 1 }}>
                                <Button onClick={() => setIsAdding(false)}>Cancelar</Button>
                                <LoadingButton
                                    variant="contained"
                                    onClick={handleAddMembers}
                                    loading={loading}
                                    disabled={selectedUsers.length === 0}
                                >
                                    Adicionar
                                </LoadingButton>
                            </Box>
                        </Box>
                    )}
                </DialogContent>

                <DialogActions>
                    {isGroup && !isAdding && (
                        <Button color="error" onClick={handleLeaveGroupClick}>
                            Sair do Grupo
                        </Button>
                    )}
                    <Button onClick={onClose}>Fechar</Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={openConfirm}
                onClose={() => setOpenConfirm(false)}
            >
                <DialogTitle>Confirmar Ação</DialogTitle>
                <DialogContent>
                    <Typography>
                        {confirmType === 'leave'
                            ? 'Tem certeza que deseja sair deste grupo?'
                            : 'Tem certeza que deseja remover este usuário do grupo?'}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenConfirm(false)}>Cancelar</Button>
                    <Button onClick={handleConfirmAction} color="error" variant="contained">
                        Confirmar
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
